import { ComparedIcon } from '@/components/Icon/ComparedIcon'
import { ProductStockStatus } from '@/constants'
import { useStore } from '@/containers'
import type { ProductCompare, ProductDetail } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import isNil from 'lodash/isNil'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useMemo, type Dispatch, type SetStateAction } from 'react'
import CompareIcon from '../Icon/CompareIcon'
import ShareIcon from '../Icon/ShareIcon'
import ImageOptimize from '../ImageOptimize'

const StarRatings = dynamic(() => import('react-star-ratings'), {
  ssr: false,
})

interface ProductDetailHeaderProps {
  entrySummaryData: ProductDetail
  setProductCompares: (newValue: ProductCompare[]) => void
  productCompares: ProductCompare[]
  setIsOpenComparePopup: Dispatch<SetStateAction<boolean>>
}

const ProductDetailHeader = ({
  entrySummaryData,
  setProductCompares,
  productCompares,
  setIsOpenComparePopup,
}: ProductDetailHeaderProps) => {
  const t = useTranslations('ProductDetail')
  const { setIsOpenShare } = useStore()

  const stockStatus = useMemo(() => {
    if (entrySummaryData?.stockStatus && !entrySummaryData?.flashSale) {
      if (entrySummaryData.stockStatus === ProductStockStatus.ARRIVING) {
        return ProductStockStatus.IN_STOCK
      }
      return entrySummaryData?.stockStatus
    }
    return ProductStockStatus.IN_STOCK
  }, [entrySummaryData])

  const isCompare = () => {
    return !isNil(productCompares.find(e => e._id === entrySummaryData?._id))
  }

  const handleAddToCompare = () => {
    const productCompared = productCompares.find(
      e => e.catId && e.catId !== entrySummaryData?.catId
    )
      ? [{}, {}, {}]
      : productCompares
    if (isCompare()) {
      setProductCompares(
        productCompared
          .map(e => (e._id && e._id === entrySummaryData?._id ? {} : e))
          .sort((a, b) => {
            if (Object.keys(a).length === 0 && Object.keys(b).length === 0) {
              return 0
            }
            if (Object.keys(a).length === 0) return 1
            if (Object.keys(b).length === 0) return -1
            return 0
          })
      )
    } else {
      const emptyIndex = productCompared.findIndex(
        e => Object.keys(e).length === 0
      )
      productCompared[
        emptyIndex > -1 ? emptyIndex : productCompared.length - 1
      ] = {
        _id: entrySummaryData?._id,
        image: entrySummaryData?.images[0],
        name: getTranslate(entrySummaryData?.name),
        catId: entrySummaryData?.catId,
        link:
          getTranslate(entrySummaryData?.slugs.value) +
          entrySummaryData?.slugs.postfix,
      }
      setProductCompares([...productCompared])
    }
    setIsOpenComparePopup(true)
    return
  }

  const scrollIntoEvaluate = () => {
    const element = document.getElementById('reviewAndRate')
    return element?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start',
    })
  }

  return (
    <div className='entry-title pb-5'>
      {!!entrySummaryData?.shouldBuy && !entrySummaryData?.bestSelling && (
        <span className='mb-1 block w-fit rounded bg-secondary px-2 py-1 text-xs font-semibold uppercase text-white'>
          Nên mua
        </span>
      )}

      {!entrySummaryData?.bestSelling && (
        <span className='mb-1 block w-fit rounded bg-secondary px-2 py-1 text-xs font-semibold uppercase text-white'>
          Bán chạy
        </span>
      )}

      <h3 className='text-2xl font-bold text-[#010103]'>
        {getTranslate(entrySummaryData?.name)}
      </h3>
      <div className='mt-1 flex items-center gap-3'>
        <div
          className='flex cursor-pointer items-center'
          onClick={() => scrollIntoEvaluate()}
        >
          <span className='-mt-[3px]'>
            <StarRatings
              starRatedColor='#FFCD4B'
              numberOfStars={5}
              rating={entrySummaryData?.rate || 65}
              starDimension='10px'
              starSpacing='1px'
              svgIconPath='M20 0L26.1803 13.1672L40 15.2786L30 25.5279L32.3607 40L20 33.1672L7.63932 40L10 25.5279L0 15.2786L13.8197 13.1672L20 0Z'
              svgIconViewBox='0 0 40 40'
            />
          </span>
          <span className='ml-2 text-xs text-[#707070]'>(đánh giá)</span>
        </div>

        <div
          onClick={() => handleAddToCompare()}
          className='ml-1 flex cursor-pointer items-center'
        >
          {isCompare() ? <ComparedIcon /> : <CompareIcon />}
          <span className='ml-2 text-xs text-[#003DD4]'>
            {isCompare() ? 'Đã thêm so sánh' : 'So sánh'}
          </span>
        </div>
        <div className='h-4 w-[1px] bg-[#707070]' />
        <div
          className={`${
            stockStatus === ProductStockStatus.IN_STOCK
              ? 'text-green-800'
              : 'text-orange'
          } text-xs font-semibold`}
        >
          {t(stockStatus)}
        </div>
        <div className='grow' />
        <div
          onClick={() => setIsOpenShare(true)}
          className='flex h-6 cursor-pointer items-center gap-2 whitespace-nowrap rounded bg-primary fill-white px-2 text-xs text-white'
        >
          <ShareIcon />
          <span>Chia sẻ</span>
        </div>
      </div>

      {!!entrySummaryData?.outstandingFeatures?.length && (
        <div className='font-sm mt-4 flex flex-wrap gap-4 gap-y-1 text-black-400'>
          {entrySummaryData.outstandingFeatures.map(item => (
            <div
              className='flex w-[calc(50%-8px)] items-start gap-2'
              key={item._id}
            >
              <ImageOptimize
                src='/assets/icons/checked.svg'
                width={10}
                height={10}
                alt='icon'
                className='mt-1'
              />
              <span>{getTranslate(item.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
export default ProductDetailHeader
